import { MenuIcon } from "@xola/icons";
import { Spinner, Table } from "@xola/ui-kit";
import clsx from "clsx";
import { orderBy } from "lodash";
import React, { useEffect, useMemo } from "react";
import { DragDropContext, Draggable, Droppable, DroppableProvided, DropResult } from "react-beautiful-dnd";
import { Experience, PrioritizedResourceID, Resource, ResourceCreatedBy } from "types";
import {
    AssignedEquipmentDetails,
    ExperienceCount,
    getExperienceAssignedToResourceRecord,
} from "./EquipmentManagement";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { useExperiences } from "@/hooks/resources/useProduct";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import { ZoomBadge } from "@/pages/Resources/EditEquipmentPage";
import { reorder } from "@/utils/reorder";

interface EquipmentDragAndDropListProps {
    experience?: Experience;
    setPrioritizedResources: (value: PrioritizedResourceID[]) => void;
    setCustomOrderChanged: (value: boolean) => void;
    prioritizedResources?: PrioritizedResourceID[];
}

export const EquipmentDragAndDropList = ({
    experience,
    setPrioritizedResources,
    setCustomOrderChanged,
    prioritizedResources = [],
}: EquipmentDragAndDropListProps) => {
    const { data: resources, isLoading } = useInfiniteCollection<Resource>("resources");
    const { data: experiences, isLoading: isLoadingExperiences } = useExperiences(null, {
        enabled: !isLoading && resources.length > 0,
    });
    const listings = useMemo(() => getExperienceAssignedToResourceRecord(experiences), [experiences]);

    const filteredResources = experience ? getResourcesAssignedToExperience(experience, resources) : resources;

    const sortedResources =
        prioritizedResources.length > 0
            ? sortResourcesByPriority(filteredResources, prioritizedResources)
            : orderBy(filteredResources, [(resource) => resource.name.toLowerCase()]);

    // set default Prioritized Resources if prioritizedResources is empty array and sortedResources has value
    const setDefaultPrioritizedResources = sortedResources.length > 0 && prioritizedResources.length === 0;
    useEffect(() => {
        if (setDefaultPrioritizedResources) {
            setPrioritizedResources(getPrioritizedResources(sortedResources));
        }
    }, [setDefaultPrioritizedResources, sortedResources, setPrioritizedResources]);

    if (isLoading) {
        return <Spinner size="small" />;
    }

    if (experience && filteredResources.length === 0) {
        return <div>No equipment assigned to this Experience</div>;
    }

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const reordered = reorder(sortedResources, result.source.index, result.destination.index);
        setPrioritizedResources(getPrioritizedResources(reordered));
        setCustomOrderChanged(true);
    };

    return (
        <div>
            <fieldset>
                <DragDropContext onDragEnd={async (result: DropResult) => handleDragEnd(result)}>
                    <Droppable droppableId="equipmentDragAndDropList">
                        {(provided: DroppableProvided) => (
                            <div ref={provided.innerRef} className="space-y-4" {...provided.droppableProps}>
                                <Table>
                                    <Table.Head>
                                        <Table.Row>
                                            <Table.Header className="!text-sm" />
                                            <Table.Header className="!text-sm">Name</Table.Header>
                                            <Table.Header className="text-center !text-sm">Count</Table.Header>
                                            <Table.Header className="text-center !text-sm">Capacity</Table.Header>
                                            <Table.Header className="!text-sm">Products</Table.Header>
                                        </Table.Row>
                                    </Table.Head>

                                    <Table.Body isStriped={false}>
                                        {sortedResources.map((resource, index) => (
                                            <EquipmentDragAndDropRow
                                                key={resource.id}
                                                resource={resource}
                                                index={index}
                                                isLoadingExperiences={isLoadingExperiences}
                                                experienceCount={listings[resource.id]}
                                            />
                                        ))}
                                        {provided.placeholder}
                                    </Table.Body>
                                </Table>
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </fieldset>
        </div>
    );
};

interface EquipmentDragAndDropRowProps {
    resource: Resource;
    index: number;
    experienceCount: ExperienceCount;
    isLoadingExperiences: boolean;
}

export const EquipmentDragAndDropRow = ({
    resource,
    index,
    experienceCount,
    isLoadingExperiences,
}: EquipmentDragAndDropRowProps) => {
    return (
        <Draggable key={resource.id} draggableId={resource.id} index={index}>
            {({ draggableProps, dragHandleProps, innerRef }, snapshot) => (
                <tr
                    ref={innerRef}
                    {...draggableProps}
                    {...dragHandleProps}
                    className={clsx("justify-between", {
                        shadow: snapshot.isDragging,
                    })}
                >
                    <Table.Cell className="w-1/12">
                        <span className="inline-flex rounded px-1 py-0.5">
                            <MenuIcon size="small" />
                        </span>
                    </Table.Cell>

                    <Table.Cell className="w-1/3">
                        <NoTranslate as="p" className="truncate whitespace-normal break-all tracking-tightest">
                            {resource.name}
                        </NoTranslate>
                        {resource.createdBy?.type === ResourceCreatedBy.ZOOM && <ZoomBadge />}
                    </Table.Cell>

                    <Table.Cell className="w-1/4 text-center">
                        <NoTranslate>{resource.count}</NoTranslate>
                    </Table.Cell>

                    <Table.Cell className="w-1/4 text-center">
                        <NoTranslate>{resource.capacity}</NoTranslate>
                    </Table.Cell>

                    <Table.Cell className="w-1/4">
                        <AssignedEquipmentDetails isLoading={isLoadingExperiences} experienceCount={experienceCount} />
                    </Table.Cell>
                </tr>
            )}
        </Draggable>
    );
};

export const sortResourcesByPriority = (resources: Resource[], prioritizedResources: PrioritizedResourceID[]) => {
    // Create a map of resource ID to priority
    // ["RESOURCEID1" : 100, "RESOURCEID2" : 200]
    const priorityMap: Record<ID, number> = {};
    for (const { resource, priority } of prioritizedResources) {
        priorityMap[resource.id] = priority;
    }

    return orderBy(
        resources,
        [
            (resource) => priorityMap[resource.id] ?? Number.POSITIVE_INFINITY, // sort by priority
            (resource) => resource.name.toLowerCase(), // for rest of equipments without priority sort by name
        ],
        ["asc", "asc"],
    );
};

const getPrioritizedResources = (resources: Resource[]): PrioritizedResourceID[] => {
    return resources.map((resource, index) => ({
        resource: {
            id: resource.id,
        },
        // instead of 1,2,3, etc order, multiply by 100 so that it is easy to modify priority if needed; Eg. To add manually a new Recource to 2nd place just change its priority to 101 (btw 100 - 200)
        priority: (index + 1) * 100,
    }));
};

const getResourcesAssignedToExperience = (experience: Experience, resources: Resource[]) => {
    const resourceIdsFromExperience = new Set((experience?.resources || []).map((resource) => resource.resource.id));
    return resources.filter((resource) => resourceIdsFromExperience.has(resource.id));
};
